import * as React from 'react'
import { theme } from '../theme'

interface Props {
    onUpload: (file: File) => void
    className?: string
    style?: React.CSSProperties
}

export const UploadImageThumbnail = ({
    onUpload,
    className,
    style,
}: Props) => {
    const [isHovered, setIsHovered] = React.useState(false)
    const [isDragging, setIsDragging] = React.useState(false)
    const fileInputRef = React.useRef<HTMLInputElement>(null)

    const handleClick = () => {
        fileInputRef.current?.click()
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
            onUpload(files[0])
            // Reset the input value so the same file can be uploaded again if needed
            e.target.value = ''
        }
    }

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        const files = e.dataTransfer.files
        if (files && files.length > 0) {
            onUpload(files[0])
        }
    }

    // Define colors directly to avoid type issues
    const primaryColor = typeof theme.colors.primary === 'string' ? theme.colors.primary : '#0066FF'
    const borderColor = typeof theme.colors.border === 'string' ? theme.colors.border : '#E5E7EB'
    const backgroundColor = typeof theme.colors.background === 'string' ? theme.colors.background : '#F3F4F6'
    const surfaceColor = typeof theme.colors.surface === 'string' ? theme.colors.surface : '#FFFFFF'
    const textColor = '#111827'
    const textSecondaryColor = '#6B7280'

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: 'none' }}
            />
            <div
                onClick={handleClick}
                className={className}
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDragging
                        ? `rgba(0, 102, 255, 0.1)`
                        : isHovered
                            ? backgroundColor
                            : surfaceColor,
                    border: `1px dashed ${
                        isDragging
                            ? primaryColor
                            : isHovered
                                ? primaryColor
                                : borderColor
                    }`,
                    borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-out',
                    overflow: 'hidden',
                    flexShrink: 0,
                    position: 'relative',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        padding: '16px',
                        boxSizing: 'border-box',
                    }}
                >
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={isDragging || isHovered ? primaryColor : textSecondaryColor}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginBottom: '8px' }}
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div
                        style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: isDragging || isHovered ? primaryColor : textColor,
                            textAlign: 'center',
                        }}
                    >
                        {isDragging ? 'Drop image here' : 'Upload Image'}
                    </div>
                    <div
                        style={{
                            fontSize: '12px',
                            color: textSecondaryColor,
                            marginTop: '4px',
                            textAlign: 'center',
                        }}
                    >
                        Drag & drop or click to browse
                    </div>
                </div>
            </div>
        </>
    )
}
